// Worker-specific data for WorkerApp
// GPS coordinates, checklists, and shift configurations

export interface Building {
  id: string;
  name: string;       // Official facility name (used on invoices)
  shortName?: string; // Abbreviation shown in UI (RPYCC, PDIC, etc.)
  address: string;
  status: 'active' | 'inactive' | 'special';
  note?: string;
  restricted?: boolean;
  window?: string;
  lat?: number;
  lng?: number;
  leed?: boolean;
  mergedInto?: string; // shown and checked into under this building instead
}

export interface Profile {
  id?: string;
  role?: string;
  assigned_buildings?: string[];
  full_name?: string;
  /** Matches ScheduleManager / profiles.shift: 'day1' | 'day2' | 'night' */
  shift?: string | null;
  /** Optional per-day override, e.g. { monday: 'day1', saturday: 'night' }.
   *  Days not present fall back to `shift`. Use resolveShiftForDay() to read. */
  weekly_shifts?: Record<string, string> | null;
}

const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

/** Which day-of-week to resolve a shift for. Defaults to today; pass a Date for other days. */
export function getDayName(date: Date = new Date()): string {
  return DAY_NAMES[date.getDay()];
}

/**
 * Resolves a worker's shift for a given day: checks weekly_shifts[day] first
 * (for workers with a split/varying schedule), falls back to the single
 * `shift` field (for workers with one consistent shift — the common case,
 * unaffected by this feature). Returns null if neither is set for that day.
 */
export function resolveShiftForDay(profile: Profile | null | undefined, date: Date = new Date()): string | null {
  if (!profile) return null;
  const day = getDayName(date);
  const override = profile.weekly_shifts?.[day];
  if (override) return override;
  return profile.shift || null;
}

export interface GPSResult {
  lat?: number;
  lng?: number;
  accuracy?: number;
  distance_metres?: number;
  verified: boolean;
  error?: string;
}

export interface FreqLabel {
  label: string;
  color: string;
}

export interface BuildingShiftConfig {
  showFilter: boolean;
  defaultFreqs: string[];
  extraOptions: string[];
  label: string | null;
}

/** Mismatch flag threshold — indoor arenas need a generous radius */
export const GPS_RADIUS_METRES = 200;

export function getDistanceMetres(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371000;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng/2) * Math.sin(dLng/2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
}

/** Never throws / never blocks the caller — resolves with null coords on deny/timeout/failure. */
export async function captureLocation(building: Building): Promise<GPSResult> {
  return new Promise(resolve => {
    if (!navigator.geolocation) return resolve({ verified: false, error: 'no_geolocation' });
    navigator.geolocation.getCurrentPosition(
      pos => {
        const dist = building.lat && building.lng
          ? Math.round(getDistanceMetres(pos.coords.latitude, pos.coords.longitude, building.lat, building.lng))
          : null;
        resolve({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: Math.round(pos.coords.accuracy),
          distance_metres: dist ?? undefined,
          verified: dist !== null && dist <= GPS_RADIUS_METRES,
          error: undefined,
        });
      },
      err => resolve({ verified: false, error: err.message || 'unknown_error' }),
      { timeout: 10000, maximumAge: 0, enableHighAccuracy: true }
    );
  });
}

/**
 * Same as captureLocation, but tries again immediately (back-to-back, no
 * delay) if the first attempt comes back with no fix at all — a cold GPS
 * lock commonly clears on the second try. Still resolves with a no-fix
 * result if every attempt fails; never blocks the caller indefinitely.
 */
export async function captureLocationRetrying(building: Building, attempts = 2): Promise<GPSResult> {
  let result = await captureLocation(building);
  for (let i = 1; i < attempts && result.lat == null; i++) {
    result = await captureLocation(building);
  }
  return result;
}

export const BUILDINGS: Building[] = [
  { id: 'A',  name: 'Town Hall',                                    address: '28 McNamara Drive, Paradise NL',    status: 'inactive', note: '⚠️ Fire remediation', window: 'Mon–Fri · 5:00pm – 12:00am', lat: 47.5271, lng: -52.8840 },
  { id: 'B',  name: 'Temporary Town Depot',                         address: '35 Glencoe Drive, Paradise NL',     status: 'active',   window: 'Mon–Thu + Sun · 5:00pm – 12:00am', lat: 47.5271, lng: -52.8840 },
  { id: 'C',  name: 'Depot Portable Trailer',                       address: '28 McNamara Drive, Paradise NL',    status: 'active',   window: 'Mon–Fri · 5:00pm – 12:00am', lat: 47.5271, lng: -52.8840 },
  // Was flagged inactive/removed here, but the Town's actual weekly
  // submissions (see supabase-week-2026-08-17.sql) keep scheduling this
  // building every week — confirmed still active and cleaned weekly,
  // day/time varies by week rather than the old fixed Monday slot.
  { id: 'D',  name: 'Wastewater Treatment Plant',                   address: '619 St. Thomas Line, Paradise NL',  status: 'active',   restricted: true, window: 'Per rental schedule · Office & Washroom only', lat: 47.5133, lng: -52.8537 },
  // 2026-09-09: lat/lng corrected — the old value (47.5310, -52.8760) was
  // ~966-998m from the real building, confirmed by three different workers'
  // GPS readings clustering tightly on separate nights (April/Omar/Sal) and
  // matching Sarah Davis Way's actual OSM geocode. New value is the average
  // of those three real on-site readings.
  { id: 'E',  name: 'Paradise Double Ice Complex & Rotary Paradise Youth & Community Centre', shortName: 'PDIC + RPYCC',     address: '1 Sarah Davis Way, Paradise NL',    status: 'active',   leed: true, window: '7 days · 8:00am – 6:00am (3 shifts)', lat: 47.52334, lng: -52.86935 },
  { id: 'F',  name: 'Rotary Paradise Youth & Community Centre', shortName: 'RPYCC',    address: '1 Sarah Davis Way, Paradise NL',    status: 'active',   mergedInto: 'E', leed: true, window: '7 days · 8:00am – 6:00am (3 shifts)', lat: 47.52334, lng: -52.86935 },
  // Names for G/H/I/J/K below were renamed to match what the Town actually
  // calls these buildings on its own schedule submissions (TownPortalApp.js
  // RENTAL_BUILDINGS + the DB's buildings table) — was previously using
  // different "contract-style" names here that didn't match, which could
  // confuse workers cross-referencing the Town's weekly schedule notes.
  // lib/data.js (invoicing) and WeeklyReport.js intentionally still use the
  // old contract-style names for now — not touched pending a decision on
  // whether those need to match the contract's official wording.
  { id: 'G',  name: 'Paradise Park Change Rooms',      shortName: 'Park Change Rooms', address: 'Sarah Davis Way, Paradise NL',       status: 'active',   window: '7 days · 9:00am – 2:00am', lat: 47.5318, lng: -52.8748 },
  { id: 'H',  name: 'Dianne Whalen Memorial Soccer Complex', shortName: 'Soccer Complex',  address: 'McNamara Drive, Paradise NL',        status: 'active',   window: 'Per rental schedule', lat: 47.5265, lng: -52.8831 },
  { id: 'I',  name: 'St. Thomas Line Community',       shortName: 'St. Thomas Line', address: '2 Neary Road, Paradise NL',          status: 'active',   window: 'Per rental schedule', lat: 47.5089, lng: -52.8601 },
  { id: 'J',  name: 'Milton Clubhouse',                shortName: 'Milton Clubhouse', address: 'Milton Road, Paradise NL',         status: 'active',   window: 'Per rental schedule · min. 2 hrs × 2 staff', lat: 47.5241, lng: -52.8795 },
  { id: 'K',  name: 'Peter Barry Duff Building',       shortName: 'PBD Building', address: '272 Topsail Pond Road, Paradise NL', status: 'active',   window: 'Per rental schedule · Outside WC: 9:30pm daily', lat: 47.5198, lng: -52.8712 },
  { id: 'L',  name: 'St. Thomas Line Pump Station',               address: '3 St. Thomas Line, Paradise NL',    status: 'active',   restricted: true, window: 'Mondays · 8:00am – 11:00am', lat: 47.5133, lng: -52.8537 },
  { id: 'M',  name: '35 Glenco Drive (Rented Town Hall Space)',    shortName: 'Glenco Dr',    address: '35 Glenco Drive, Paradise NL',      status: 'active',   window: 'Per rental schedule', lat: 47.5523, lng: -52.7891 },
  { id: 'N',  name: '1655 Topsail Road (Rented Town Hall Space)',  shortName: 'Topsail Rd',   address: '1655 Topsail Road, Paradise NL',    status: 'active',   window: 'Per rental schedule', lat: 47.5089, lng: -52.8601 },
  // O and P are outside-washroom cleaning duties the Town submits every
  // week (see supabase-week-2026-08-17.sql) — distinct tasks/times from
  // Building G (indoor concession) and Building K (indoor park building)
  // respectively, not duplicates. Whether these are billed separately from
  // G/K or already bundled into those monthly rates hasn't been confirmed
  // yet, so they're not added to lib/data.js's billing list — just tracked
  // here so they display and can be assigned like any other building.
  { id: 'O',  name: 'Paradise Park (Outside Washrooms)',           shortName: 'Park Outside WC', address: 'Sarah Davis Way, Paradise NL',      status: 'active',   window: 'Per rental schedule', lat: 47.5318, lng: -52.8748 },
  { id: 'P',  name: 'Peter Barry Duff Washrooms',                  shortName: 'PBD Washrooms',   address: '272 Topsail Pond Road, Paradise NL', status: 'active',  window: 'Per rental schedule', lat: 47.5198, lng: -52.8712 },
  // Q: the Soccer Complex's own outdoor washroom (cleaned after 9:30pm daily,
  // year-round) — a separate standing duty from building H's rental-based
  // cleanings, same pattern as O and P above. Found missing from the app
  // entirely (previously buried as a text note on H) when cross-checking
  // the Aug 17 Town schedule doc line by line.
  { id: 'Q',  name: 'Outdoor Washroom – Dianne Whalen Memorial Soccer Complex', shortName: 'Soccer Complex Outside WC', address: 'McNamara Drive, Paradise NL', status: 'active', window: 'Per rental schedule', lat: 47.5265, lng: -52.8831 },
  { id: 'SE', name: 'Special Event',                               address: 'Various Town Facilities',           status: 'special',  window: 'Per event schedule · Min. 2 staff · Track hours carefully' },
];

export const FREQ_LABELS: Record<string, FreqLabel> = {
  'twice-daily': { label: 'Twice Daily', color: '#22c55e' },
  'nightly': { label: 'Nightly', color: '#3b82f6' },
  'daily': { label: 'Daily', color: '#22c55e' },
  'after-rental': { label: 'After Rental', color: '#3b82f6' },
  'weekly': { label: 'Weekly', color: '#a78bfa' },
  'monthly': { label: 'Monthly', color: '#f59e0b' },
  'semi-annual': { label: '6-Month', color: '#f97316' },
  'annual': { label: 'Annual', color: '#ef4444' },
};

// Which buildings need the filter bar, and what their default active freqs are
// Buildings not listed here are simple daily-only — no filter bar shown
export const BUILDING_SHIFT_CONFIG: Record<string, BuildingShiftConfig> = {
  E: { showFilter: false, defaultFreqs: ['twice-daily', 'nightly'], extraOptions: ['after-rental'], label: null },
  F: { showFilter: false, defaultFreqs: ['twice-daily', 'nightly'], extraOptions: ['after-rental'], label: null },
  G: { showFilter: true, defaultFreqs: ['after-rental'], extraOptions: [], label: null },
  H: { showFilter: true, defaultFreqs: ['after-rental'], extraOptions: [], label: null },
  I: { showFilter: true, defaultFreqs: ['after-rental'], extraOptions: [], label: null },
  J: { showFilter: true, defaultFreqs: ['after-rental'], extraOptions: [], label: null },
  K: { showFilter: true, defaultFreqs: ['twice-daily'], extraOptions: ['after-rental'], label: 'What type of shift is this?' },
  D: { showFilter: false, defaultFreqs: ['weekly'], extraOptions: [], label: null },
  L: { showFilter: false, defaultFreqs: ['weekly'], extraOptions: [], label: null },
  SE: { showFilter: false, defaultFreqs: ['daily'], extraOptions: [], label: null },
};

export function getBuildingShiftConfig(buildingId: string): BuildingShiftConfig {
  return BUILDING_SHIFT_CONFIG[buildingId] || { showFilter: false, defaultFreqs: ['daily'], extraOptions: [], label: null };
}